import { DataGrid } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartBindingValue, CustomChartTableColumn } from '@/entities/dashboards';
import { ChartCardShell } from '../ChartCardShell';
import type { ChartAppearance } from '../chartElements';
import { chartTableHeight, chartTableStyle } from '../chartLayout';
import { preventRowShiftSelection } from '../../conversationLink';
import { buildTableColumns, tableRowConversationOpener, type IndexedTableRow } from './chartTable';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';
import { useChartTableState } from './useChartTableState';

type Props = {
  /** Диаграмма, чьей таблице принадлежит личный вид: состав колонок, упорядочение и фильтры */
  chartId: string;
  title: string;
  /** Колонки таблицы в порядке выборки */
  columns: CustomChartTableColumn[];
  /** Строки таблицы с ключами и диалогами */
  rows: IndexedTableRow[];
  /** Колонка, значением которой выбранная строка наполняет связанную диаграмму */
  selectionColumn: string | undefined;
  /** Оформление диаграммы: окрашиваемые колонки, цвета их значений и правила раскраски по величине */
  appearance?: ChartAppearance;
  /** Высота диаграммы задана пользователем — таблица прокручивает строки внутри неё */
  hasFixedHeight?: boolean;
  /** Открытие диалога строки; не задано — строки к диалогам не ведут */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
  /** Выбор строки наполняет связанную диаграмму; задан — выбор заменяет переход к диалогу */
  onSelectElement?: (value: ChartBindingValue) => void;
};

/**
 * Карточка таблицы диаграммы — и простой, и с группировкой: колонки и строки приходят готовыми,
 * клиент их не пересчитывает, но показывает в личном виде смотрящего: состав и порядок колонок,
 * упорядочение и фильтры настраиваются теми же средствами кита, что в таблице «Все детали», и
 * хранятся по каждой диаграмме отдельно.
 *
 * Строка, за которой выборка назвала диалог, открывает его — как строка списка диалогов. У
 * таблицы, выбор в которой наполняет связанную диаграмму, выбор занят разбором и к диалогу не
 * ведёт; с погашенным переходом (режим правки состава) строки не ведут никуда.
 */
export const ChartTableCard: FC<Props> = ({
  chartId,
  title,
  columns: tableColumns,
  rows: indexedRows,
  selectionColumn,
  appearance,
  hasFixedHeight,
  onOpenConversation,
  onSelectElement,
}) => {
  const { t } = useTranslation('qualityDashboard');
  const openRowConversation = tableRowConversationOpener(indexedRows, onSelectElement ? undefined : onOpenConversation);
  const hasRowLinks = !!openRowConversation;
  const columns = useMemo(
    () =>
      buildTableColumns(
        tableColumns,
        indexedRows.map((row) => row.values),
        appearance,
        hasRowLinks,
      ),
    [tableColumns, indexedRows, appearance, hasRowLinks],
  );
  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);
  const { gridProps, onResetColumns, rows, sorting, onSortingChange, filters, onFiltersChange } = useChartTableState({
    chartId,
    columns: tableColumns,
    rows: indexedRows,
  });

  const selectRow =
    onSelectElement && selectionColumn
      ? (row: IndexedTableRow) => onSelectElement(row.values[selectionColumn] ?? null)
      : undefined;

  return (
    <ChartCardShell
      title={title}
      scrollable={false}
      footer={legendItems.length > 0 && <ChartLegend items={legendItems} />}
      hasFixedHeight={hasFixedHeight}
    >
      {/* Обёртка не занимает места: она только перехватывает shift+нажатие строки */}
      <div className="contents" onMouseDown={hasRowLinks ? preventRowShiftSelection : undefined}>
        <DataGrid
          {...gridProps}
          columns={columns}
          dataSource={{ mode: 'client', data: rows }}
          getRowId={(row) => row.key}
          enableColumnResizing
          enableColumnReordering
          sortMode="single"
          sorting={sorting}
          onSortingChange={onSortingChange}
          filters={filters}
          onFiltersChange={onFiltersChange}
          columnsPanel={{ onReset: onResetColumns }}
          onRowClick={selectRow ?? openRowConversation}
          height={chartTableHeight(rows.length, hasFixedHeight)}
          style={chartTableStyle(hasFixedHeight)}
        />
      </div>
    </ChartCardShell>
  );
};
